import React, { useCallback, useEffect, useMemo } from 'react';
import { WidgetPortContext } from '@kemu-io/kemu-core/types';
import { DataType } from '@kemu-io/hs-types';
import {
  GateUI,
	GateUIProps,
	GateCustomSettingsProps,
  GetPortsInformationFunction
} from '../index.js';
import styles from './fieldValue.module.css';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import GateIcon from '@components/gateIcon/gateIcon';
import useTranslation, { getTranslationFunction } from '@hooks/useTranslation';
import Icon from '@ant-design/icons';
import { ReactComponent as FieldValueWidgetIcon } from './icon.svg';
import { Menu } from 'antd';
import WidgetDropdownButton from '@components/WidgetDropdownButton/WidgetDropdownButton';
import { findWidget } from '@kemu-io/kemu-core/common/recipeCache';
import { WidgetType, WidgetGroupState } from '@kemu-io/kemu-core/types';
import FieldValueWidgetProcessor, { FieldValueWidgetState } from '@kemu-io/kemu-core/widgets/fieldValue/index.js';
import {
	ReadInputName,
	SetValueInputName,
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/fieldValue/index.js';
import { calculateWidgetColors } from '../common';
import KemuSwitch from '@components/form-control/kemuSwitch/KemuSwitch';
import { SETTINGS_CONTAINER_CLASS } from '@common/constants';
import classNames from 'classnames';
import kemuCore from '@kemu-io/kemu-core';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';

type Props = GateUIProps

const FieldValueWidget = (props: Props) => {
  const { recipeId, thingRecipeId, info, repaintExistingPorts } = props;
  const [state, setState] = useReactiveWidgetState<FieldValueWidgetState>(recipeId, thingRecipeId, info.id);
  const t = useTranslation('LogicMapper.Widgets.FieldValue');

	const fixedState = {
		...getDefaultState(),
		...state
	};

	/**
	 * Finds the immediate parent Custom Widget (widgetGroup) by traversing up the hierarchy using groupId
	 */
	const findParentCustomWidget = useCallback(() => {
		const currentWidget = findWidget({ recipeId, widgetId: info.id });
		if (!currentWidget) {
			return null;
		}

		const groupId = currentWidget.groupId;
		if (!groupId) {
			return null;
		}

    const parentWidget = findWidget({ recipeId, widgetId: groupId });
    if (!parentWidget) {
      return null;
    }

    // Check if this is a Custom Widget (widgetGroup)
    if (parentWidget.type === WidgetType.widgetGroup) {
      return parentWidget;
    }
		

		return null;
	}, [recipeId, thingRecipeId, info.id]);

	/**
	 * Gets available setting fields from the parent Custom Widget
	 */
	const availableFields = useMemo(() => {
		const parentWidget = findParentCustomWidget();
		if (!parentWidget) {
			return [];
		}

		const parentState = parentWidget.state as WidgetGroupState;
		const settings = parentState.settings || [];

		// Filter to only show fields (exclude groups - though groups aren't in settings anyway)
		// Settings are already fields, so we just return them
		return settings;
	}, [findParentCustomWidget]);

	/**
	 * Handles menu item selection
	 */
	const handleMenuClick = useCallback((e: any) => {
		const selectedFieldName = e.key as string;
		setState((s) => ({
			...s,
			fieldName: selectedFieldName,
		}));
	}, [setState, repaintExistingPorts]);

	/**
	 * Gets the display name for the selected field
	 */
	const displayName = useMemo(() => {
		if (!fixedState.fieldName) {
			return t('NoFieldSelected', 'No field selected');
		}

		const field = availableFields.find(f => f.variableName === fixedState.fieldName);
		if (!field) {
			return t('NoFieldSelected', 'No field selected');
		}

		return field.variableName;
	}, [fixedState.fieldName, availableFields, t]);

	const parentWidget = findParentCustomWidget();
	const hasParent = parentWidget !== null;

  // Repaint the ports when the field name changes
  useEffect(() => {
    if(fixedState.fieldName) { 
      repaintExistingPorts();
    }
  }, [fixedState.fieldName, repaintExistingPorts]);

  // Re-initialize widget when field name changes to update subscription
  const reinitializeWidget = useCallback(() => {
    kemuCore.initializeWidget({
      recipePoolId: recipeId,
      thingRecipeId: DEFAULT_THING_ID,
      widgetRecipeId: info.id,
    });
  }, [recipeId, info.id]);

  useEffect(() => {
    if (fixedState.fieldName) {
      reinitializeWidget();
    }
  }, [fixedState.fieldName, reinitializeWidget]);

	const menu = (
		<Menu onClick={handleMenuClick} selectedKeys={[fixedState.fieldName || '']}>
			{!hasParent ? (
				<Menu.Item key="no-parent" disabled>
					{t('NoParentWidget', 'Not inside a Custom Widget')}
				</Menu.Item>
			) : availableFields.length === 0 ? (
				<Menu.Item key="no-fields" disabled>
					{t('NoFieldsAvailable', 'No fields available')}
				</Menu.Item>
			) : (
				availableFields.map((field) => (
					<Menu.Item key={field.variableName}>
						{field.variableName}
					</Menu.Item>
				))
			)}
		</Menu>
	);

	// Only show widget when inside a Custom Widget
	if (!hasParent) {
		return (
			<div className={styles.GateBody}>
				<div className={styles.NoParentMessage}>
					{t('NoParentWidget', 'Not inside a Custom Widget')}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.GateBody}>
			<WidgetDropdownButton
				widgetType="storage"
				disabled={props.info.disabled}
				name={displayName}
				menu={menu}
				wrapperClassName={styles.DropDown}
				overlayClassName={styles.DropdownOverlay}
			/>
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	// Use a simple icon - you can replace this with an SVG icon later
	return (
		<GateIcon icon={<Icon component={FieldValueWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<FieldValueWidgetState>(props.recipeId, props.blockId, props.gateInfo.id);
	const fixedState = {
		...getDefaultState(),
		...state
	};

	const t = useTranslation('LogicMapper.Widgets.FieldValue.Settings');

	/**
	 * Handles reactive switch change and re-initializes the widget
	 */
	const handleSwitchChange = async (checked: boolean) => {
		setState({ ...fixedState, reactive: checked });
		
		// Re-initialize the widget to properly subscribe/unsubscribe from variable changes
		await kemuCore.initializeWidget({
			recipePoolId: props.recipeId,
			thingRecipeId: DEFAULT_THING_ID,
			widgetRecipeId: props.gateInfo.id,
		});
	};

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}

			<div className={styles.SwitchContainer}>
				<label>{t('ReactiveSwitch', 'Auto-emit on field change')}</label>
				<KemuSwitch
					size="small"
					checked={fixedState.reactive}
					onChange={handleSwitchChange}
				/>
			</div>
		</div>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state, widgetInfo, intl) => {
	const t = getTranslationFunction(intl, 'LogicMapper.Widgets.FieldValue.Port');
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = FieldValueWidgetProcessor.getOutputNames(state, portContext);
  
	return {
		inputs: [
			{
				name: ReadInputName,
				type: DataType.Anything,
				position: [0, 0.3, -1, 0],
				label: t('read', 'read'),
			},
			{
				name: SetValueInputName,
				type: DataType.Anything,
				position: [0, 0.7, -1, 0],
				label: t('setValue', 'set value'),
			}
		],
		outputs: [{
			name: outputNames[0].name,
			type: outputNames[0].type,
			position: [1, 0.5, 1, 0],
			label: outputNames[0].label,
		}],
	};
};

// Get widget color (yellow for storage type)
const yellowColor = '#FFD700';
const widgetColors = calculateWidgetColors(yellowColor);

export default {
	getPortsInformation,
  hasTitle: true,
	BarIcon: GateBarIcon,
	Element: FieldValueWidget,
	CustomSettingsDialog: GateCustomSettings,
  getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.FieldValue.WidgetTitle' }),
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.FieldValue.BarTitle' }),
	getSelectionColor: () => widgetColors.content,
} as GateUI;

